import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { ParametresAccessoires, ParametresFigureHumaine, ParametresEclairage, ParametresVegetation, ParametresMobilier, TypeMobilier, StructureAccessoires, ElementMobilier, TypeFigureHumaine } from '../types';
import { Accessoires3DService } from '../services/Accessoires3DService';

// Styles CSS intégrés - Thème sombre professionnel cohérent
const sectionStyle: React.CSSProperties = {
  marginBottom: 16,
  padding: 15,
  border: '1px solid #404040',
  borderRadius: 8,
  background: '#2d2d2d'
};

const labelStyle: React.CSSProperties = { 
  display: 'block', 
  fontSize: 13, 
  marginBottom: 6, 
  color: '#e0e0e0',
  fontWeight: '500'
};
const rowStyle: React.CSSProperties = { 
  display: 'flex', 
  gap: 8, 
  alignItems: 'center', 
  marginBottom: 8 
};
const fieldStyle: React.CSSProperties = { flex: 1 };

// Utils: simple debounce without extra deps
function useDebouncedCallback<T extends (...args: any[]) => void>(cb: T, delay: number) {
  const timeoutRef = useRef<number | null>(null);
  const cbRef = useRef(cb);
  cbRef.current = cb;
  return useCallback((...args: Parameters<T>) => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => cbRef.current(...args), delay);
  }, [delay]);
}

// Valeurs par défaut alignées avec les interfaces
const defaultFigure: ParametresFigureHumaine = {
  actif: false,
  type: TypeFigureHumaine.HOMME_DEBOUT,
  position: { x: 0, y: 0, z: 0 },
  rotation: 0,
  echelle: 1,
  couleur: '#c4a484',
  visible: true
};

const defaultEclairage: ParametresEclairage = {
  actif: true,
  ambiance: 0.5, // Midi par défaut (lumière naturelle)
  // Éclairages 3D spécifiques uniquement
  lampeCentrale: {
    actif: false,
    intensite: 1.0,
    couleur: '#ffdd88',
    taille: 1.0,
    hauteur: 0.8, // lampe plus proche du toit par défaut
    angle: Math.PI / 3, // 60°
    fil: true
  },
  barresLEDVerticales: {
    actif: false,
    intensite: 0.8,
    couleur: '#ffffff',
    nombreLEDs: 10
  },
  barresLEDHorizontales: {
    actif: false,
    intensite: 0.8,
    couleur: '#ffffff',
    nombreLEDs: 20
  },
  spotsDirectionnels: {
    actif: false,
    nombre: 4,
    intensite: 1.2,
    couleur: '#ffffff',
    angle: 0.5
  },
  guirlandeLED: {
    actif: false,
    intensite: 0.6,
    couleur: '#ffaa44',
    nombreAmpoules: 20,
    effet: 'fixe'
  }
};

const defaultVegetation: ParametresVegetation = {
  actif: false,
  plantesGrimpantes: { 
    actif: false, 
    surMontants: true,
    surTraverses: false,
    hauteur: 0.8, 
    densite: 0.5, 
    couleur: '#228B22' 
  },
  arbustes: { 
    actif: false, 
    positions: [], 
    type: 'arbuste-petit',
    taille: 0.3, 
    couleur: '#2E8B57' 
  },
  decoration: {
    actif: false,
    fleurs: false,
    herbe: false,
    couleurFleurs: '#FF69B4',
    couleurHerbe: '#90EE90'
  }
};

const defaultMobilier: ParametresMobilier = {
  actif: false,
  elements: [],
  modeles: {
    [TypeMobilier.TABLE]: { longueur: 1.4, largeur: 0.7, hauteur: 0.75, couleur: '#8B4513' },
    [TypeMobilier.CHAISE]: { largeur: 0.42, profondeur: 0.42, hauteur: 0.9, hauteurAssise: 0.45, couleur: '#654321' },
    [TypeMobilier.CANAPE]: { largeur: 1.6, profondeur: 0.7, hauteur: 0.8, couleur: '#4682B4' },
    [TypeMobilier.BANC]: { largeur: 1.2, profondeur: 0.4, hauteur: 0.5, couleur: '#8B4513' },
    [TypeMobilier.PARASOL]: { diametre: 2.0, hauteur: 2.1, couleur: '#FF6347' }
  }
};

export const AccessoiresModule: React.FC<{
  onChange?: (params: ParametresAccessoires) => void;
  onStructureChange?: (structure: StructureAccessoires) => void;
  hauteurStructure?: number;
}> = ({ onChange, onStructureChange, hauteurStructure = 2.2 }) => {
  const [params, setParams] = useState<ParametresAccessoires>({
    actif: false,
    figureHumaine: defaultFigure,
    eclairage: defaultEclairage,
    vegetation: defaultVegetation,
    mobilier: defaultMobilier
  });

  // 3D service memo
  const service3d = useMemo(() => new Accessoires3DService(), []);

  const structure = useMemo<StructureAccessoires>(() => ({
    parametres: params,
    figuresHumaines: [],
    elementsMobilier: params.mobilier?.elements || [],
    donneesStructure: { 
      largeur: 4, 
      longueur: 4, 
      hauteur: hauteurStructure, 
      centre: { x: 0, y: 0, z: 0 } 
    }
  }), [params, hauteurStructure]);

  // notifier parent
  useEffect(() => {
    onChange?.(params);
    onStructureChange?.(structure);
  }, [params, structure, onChange, onStructureChange]);

  // Note: Les sliders d'éclairage mettent à jour la 3D immédiatement (onChange) pour une réactivité temps réel

  const toggle = useCallback((key: keyof ParametresAccessoires) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setParams(p => {
      if (key === 'eclairage') {
        const prevEcl = (p as any)[key] as ParametresEclairage;
        // On met à jour actif et, si on désactive, on fixe ambiance à 0.5 pour conserver un rendu jour dans la scène
        const nextEcl: ParametresEclairage = {
          ...prevEcl,
          actif: checked,
          ambiance: checked ? prevEcl.ambiance ?? 0.5 : 0.5
        };
        return { ...p, [key]: nextEcl } as ParametresAccessoires;
      }
      return ({ ...p, [key]: { ...(p as any)[key], actif: checked } }) as ParametresAccessoires;
    });
  }, []);

  const addMobilier = useCallback((type: TypeMobilier) => {
    setParams(p => {
      const nouveau: ElementMobilier = {
        id: `mob-${Date.now()}`,
        type,
        position: { x: 0, y: 0, z: 0 },
        rotation: 0,
        echelle: 1,
        couleur: '#888',
        visible: true
      } as ElementMobilier;
      return { ...p, mobilier: { ...p.mobilier!, actif: true, elements: [...(p.mobilier?.elements || []), nouveau] } };
    });
  }, []);

  const updateElement = useCallback((id: string, patch: Partial<ElementMobilier>) => {
    setParams(p => ({
      ...p,
      mobilier: {
        ...p.mobilier!,
        elements: (p.mobilier?.elements || []).map(el => el.id === id ? { ...el, ...patch } : el)
      }
    }));
  }, []);

  const removeElement = useCallback((id: string) => {
    setParams(p => ({
      ...p,
      mobilier: {
        ...p.mobilier!,
        elements: (p.mobilier?.elements || []).filter(el => el.id !== id)
      }
    }));
  }, []);

  useEffect(() => {
    setParams((prev: ParametresAccessoires) => {
      const anyActive = prev.figureHumaine.actif || prev.eclairage.actif || prev.vegetation.actif || prev.mobilier.actif;
      if (prev.actif !== anyActive) {
        return { ...prev, actif: anyActive };
      }
      return prev;
    });
  }, [params.figureHumaine.actif, params.eclairage.actif, params.vegetation.actif, params.mobilier.actif]);

return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={sectionStyle}>
        <div style={{
          fontSize: '16px',
          fontWeight: 'bold',
          marginBottom: '10px',
          color: '#4dabf7',
          borderBottom: '2px solid #4dabf7',
          paddingBottom: '3px'
        }}>Figure humaine</div>
        <label style={labelStyle}><input type="checkbox" checked={!!params.figureHumaine?.actif} onChange={toggle('figureHumaine')} /> Activer</label>
        {params.figureHumaine?.actif && (
          <div>
            <div style={rowStyle}>
              <span style={{ width: 90 }}>Rotation</span>
              <input className="slider-ambiance" style={fieldStyle} type="range" min={-180} max={180} value={params.figureHumaine.rotation} onChange={e => setParams(p => ({ ...p, figureHumaine: { ...p.figureHumaine!, rotation: parseFloat(e.target.value) } }))} />
            </div>
            <div style={rowStyle}>
              <span style={{ width: 90 }}>Echelle</span>
              <input className="slider-ambiance" style={fieldStyle} type="range" min={0.5} max={1.5} step={0.05} value={params.figureHumaine.echelle} onChange={e => setParams(p => ({ ...p, figureHumaine: { ...p.figureHumaine!, echelle: parseFloat(e.target.value) } }))} />
            </div>
            <div style={rowStyle}>
              <span style={{ width: 90 }}>Couleur</span>
              <input style={fieldStyle} type="color" value={params.figureHumaine.couleur!} onChange={e => setParams(p => ({ ...p, figureHumaine: { ...p.figureHumaine!, couleur: e.target.value } }))} />
            </div>
          </div>
        )}
      </div>

      <div style={sectionStyle}>
        <div style={{
          fontSize: '16px',
          fontWeight: 'bold',
          marginBottom: '10px',
          color: '#4dabf7',
          borderBottom: '2px solid #4dabf7',
          paddingBottom: '3px'
        }}>Éclairage</div>
        <label style={labelStyle}><input type="checkbox" checked={!!params.eclairage?.actif} onChange={toggle('eclairage')} /> Activer</label>
        {params.eclairage?.actif && (
          <div>
            {/* Ambiance générale */}
            <div style={{ marginBottom: 12, padding: 8, border: '1px solid #555', borderRadius: 4, backgroundColor: '#3a3a3a' }}>
              <div style={{ color: '#e0e0e0', fontWeight: 'bold', fontSize: '14px', marginBottom: '8px' }}>🌞 Ambiance Générale</div>
              <div style={rowStyle}>
                <input
                  className="slider-ambiance"
                  style={{ ...fieldStyle, accentColor: '#007acc' }}
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={params.eclairage.ambiance}
                  onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, ambiance: parseFloat(e.target.value) } }))}
                />
              </div>
            </div>

            {/* Séparateur pour les éclairages 3D spécifiques */}
            <div style={{ margin: '16px 0', padding: '8px', backgroundColor: '#3a3a3a', border: '1px solid #555', borderRadius: 4 }}>
              <div style={{ color: '#e0e0e0', fontWeight: 'bold', fontSize: '14px' }}>🔆 Éclairages 3D Spécifiques</div>
            </div>

            {/* Lampe centrale */}
            <div style={{ marginBottom: 12, padding: 8, border: '1px solid #555', borderRadius: 4, backgroundColor: '#3a3a3a' }}>
              <label style={labelStyle}><input type="checkbox" checked={!!params.eclairage.lampeCentrale?.actif} onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, lampeCentrale: { ...p.eclairage!.lampeCentrale!, actif: e.target.checked } } }))} /> 💡 Lampe centrale</label>
              {params.eclairage.lampeCentrale?.actif && (
                <div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Intensité</span>
                    <input className="slider-lampe" style={fieldStyle} type="range" min={0} max={2} step={0.1} value={params.eclairage.lampeCentrale.intensite}
                      onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, lampeCentrale: { ...p.eclairage!.lampeCentrale!, intensite: parseFloat(e.target.value) } } }))}
                    />
                  </div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Couleur</span>
                    <input style={fieldStyle} type="color" value={params.eclairage.lampeCentrale.couleur} onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, lampeCentrale: { ...p.eclairage!.lampeCentrale!, couleur: e.target.value } } }))} />
                  </div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Taille</span>
                    <input className="slider-lampe" style={fieldStyle} type="range" min={0.5} max={3} step={0.1} value={params.eclairage.lampeCentrale.taille}
                      onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, lampeCentrale: { ...p.eclairage!.lampeCentrale!, taille: parseFloat(e.target.value) } } }))}
                    />
                  </div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Hauteur</span>
                    <input className="slider-lampe" style={fieldStyle} type="range" min={0} max={1} step={0.01} value={params.eclairage.lampeCentrale.hauteur ?? 0.8}
                      onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, lampeCentrale: { ...p.eclairage!.lampeCentrale!, hauteur: parseFloat(e.target.value) } } }))}
                    />
                  </div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Angle</span>
                    <input className="slider-lampe" style={fieldStyle} type="range" min={0.2} max={1.4} step={0.01} value={params.eclairage.lampeCentrale.angle ?? Math.PI / 3}
                      onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, lampeCentrale: { ...p.eclairage!.lampeCentrale!, angle: parseFloat(e.target.value) } } }))}
                    />
                  </div>
                  <div style={rowStyle}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={params.eclairage.lampeCentrale.fil ?? true}
                        onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, lampeCentrale: { ...p.eclairage!.lampeCentrale!, fil: e.target.checked } } }))}
                      />
                      <span>Fil de support</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Barres LED verticales */}
            <div style={{ marginBottom: 12, padding: 8, border: '1px solid #555', borderRadius: 4, backgroundColor: '#3a3a3a' }}>
              <label style={labelStyle}><input type="checkbox" checked={!!params.eclairage.barresLEDVerticales?.actif} onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, barresLEDVerticales: { ...p.eclairage!.barresLEDVerticales!, actif: e.target.checked } } }))} /> 📏 Barres LED verticales</label>
              {params.eclairage.barresLEDVerticales?.actif && (
                <div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Intensité</span>
                    <input className="slider-ambiance" style={fieldStyle} type="range" min={0} max={2} step={0.1} value={params.eclairage.barresLEDVerticales.intensite}
                      onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, barresLEDVerticales: { ...p.eclairage!.barresLEDVerticales!, intensite: parseFloat(e.target.value) } } }))}
                    />
                  </div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Couleur</span>
                    <input style={fieldStyle} type="color" value={params.eclairage.barresLEDVerticales.couleur} onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, barresLEDVerticales: { ...p.eclairage!.barresLEDVerticales!, couleur: e.target.value } } }))} />
                  </div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Nb LEDs</span>
                    <input style={fieldStyle} type="range" min={5} max={20} step={1} value={params.eclairage.barresLEDVerticales.nombreLEDs}
                      onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, barresLEDVerticales: { ...p.eclairage!.barresLEDVerticales!, nombreLEDs: parseInt(e.target.value) } } }))}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Barres LED horizontales */}
            <div style={{ marginBottom: 12, padding: 8, border: '1px solid #555', borderRadius: 4, backgroundColor: '#3a3a3a' }}>
              <label style={labelStyle}><input type="checkbox" checked={!!params.eclairage.barresLEDHorizontales?.actif} onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, barresLEDHorizontales: { ...p.eclairage!.barresLEDHorizontales!, actif: e.target.checked } } }))} /> 📐 Barres LED horizontales</label>
              {params.eclairage.barresLEDHorizontales?.actif && (
                <div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Intensité</span>
                    <input className="slider-ambiance" style={fieldStyle} type="range" min={0} max={2} step={0.1} value={params.eclairage.barresLEDHorizontales.intensite}
                      onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, barresLEDHorizontales: { ...p.eclairage!.barresLEDHorizontales!, intensite: parseFloat(e.target.value) } } }))}
                    />
                  </div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Couleur</span>
                    <input style={fieldStyle} type="color" value={params.eclairage.barresLEDHorizontales.couleur} onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, barresLEDHorizontales: { ...p.eclairage!.barresLEDHorizontales!, couleur: e.target.value } } }))} />
                  </div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Nb LEDs</span>
                    <input style={fieldStyle} type="range" min={10} max={40} step={2} value={params.eclairage.barresLEDHorizontales.nombreLEDs}
                      onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, barresLEDHorizontales: { ...p.eclairage!.barresLEDHorizontales!, nombreLEDs: parseInt(e.target.value) } } }))}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Spots directionnels */}
            <div style={{ marginBottom: 12, padding: 8, border: '1px solid #555', borderRadius: 4, backgroundColor: '#3a3a3a' }}>
              <label style={labelStyle}><input type="checkbox" checked={!!params.eclairage.spotsDirectionnels?.actif} onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, spotsDirectionnels: { ...p.eclairage!.spotsDirectionnels!, actif: e.target.checked } } }))} /> 🔦 Spots directionnels</label>
              {params.eclairage.spotsDirectionnels?.actif && (
                <div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Nombre</span>
                    <input style={fieldStyle} type="range" min={2} max={8} step={1} value={params.eclairage.spotsDirectionnels.nombre}
                      onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, spotsDirectionnels: { ...p.eclairage!.spotsDirectionnels!, nombre: parseInt(e.target.value) } } }))}
                    />
                  </div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Intensité</span>
                    <input style={fieldStyle} type="range" min={0} max={3} step={0.1} value={params.eclairage.spotsDirectionnels.intensite}
                      onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, spotsDirectionnels: { ...p.eclairage!.spotsDirectionnels!, intensite: parseFloat(e.target.value) } } }))}
                    />
                  </div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Couleur</span>
                    <input style={fieldStyle} type="color" value={params.eclairage.spotsDirectionnels.couleur} onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, spotsDirectionnels: { ...p.eclairage!.spotsDirectionnels!, couleur: e.target.value } } }))} />
                  </div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Angle</span>
                    <input style={fieldStyle} type="range" min={0.2} max={1.5} step={0.1} value={params.eclairage.spotsDirectionnels.angle}
                      onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, spotsDirectionnels: { ...p.eclairage!.spotsDirectionnels!, angle: parseFloat(e.target.value) } } }))}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Guirlande LED */}
            <div style={{ marginBottom: 12, padding: 8, border: '1px solid #555', borderRadius: 4, backgroundColor: '#3a3a3a' }}>
              <label style={labelStyle}><input type="checkbox" checked={!!params.eclairage.guirlandeLED?.actif} onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, guirlandeLED: { ...p.eclairage!.guirlandeLED!, actif: e.target.checked } } }))} /> 🎄 Guirlande LED décorative</label>
              {params.eclairage.guirlandeLED?.actif && (
                <div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Intensité</span>
                    <input className="slider-ambiance" style={fieldStyle} type="range" min={0} max={1.5} step={0.1} value={params.eclairage.guirlandeLED.intensite}
                      onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, guirlandeLED: { ...p.eclairage!.guirlandeLED!, intensite: parseFloat(e.target.value) } } }))}
                    />
                  </div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Couleur</span>
                    <input style={fieldStyle} type="color" value={params.eclairage.guirlandeLED.couleur} onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, guirlandeLED: { ...p.eclairage!.guirlandeLED!, couleur: e.target.value } } }))} />
                  </div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Nb Ampoules</span>
                    <input style={fieldStyle} type="range" min={10} max={50} step={5} value={params.eclairage.guirlandeLED.nombreAmpoules}
                      onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, guirlandeLED: { ...p.eclairage!.guirlandeLED!, nombreAmpoules: parseInt(e.target.value) } } }))}
                    />
                  </div>
                  <div style={rowStyle}>
                    <span style={{ width: 90 }}>Effet</span>
                    <select style={fieldStyle} value={params.eclairage.guirlandeLED.effet} onChange={e => setParams(p => ({ ...p, eclairage: { ...p.eclairage!, guirlandeLED: { ...p.eclairage!.guirlandeLED!, effet: e.target.value as 'fixe' | 'clignotant' | 'progressif' } } }))}>
                      <option value="fixe">Fixe</option>
                      <option value="clignotant">Clignotant</option>
                      <option value="progressif">Progressif</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div style={sectionStyle}>
        <div style={{
          fontSize: '16px',
          fontWeight: 'bold',
          marginBottom: '10px',
          color: '#4dabf7',
          borderBottom: '2px solid #4dabf7',
          paddingBottom: '3px'
        }}>🌿 Végétation</div>
        <label style={labelStyle}><input type="checkbox" checked={!!params.vegetation?.actif} onChange={toggle('vegetation')} /> Activer</label>
        {params.vegetation?.actif && (
          <div>
            <div style={{ marginBottom: 12, padding: 8, border: '1px solid #555', borderRadius: 4, backgroundColor: '#3a3a3a' }}>
              <label style={labelStyle}><input type="checkbox" checked={!!params.vegetation.plantesGrimpantes?.actif} onChange={e => setParams(p => ({ ...p, vegetation: { ...p.vegetation!, plantesGrimpantes: { ...p.vegetation!.plantesGrimpantes!, actif: e.target.checked } } }))} /> 🌱 Plantes grimpantes</label>
            {params.vegetation.plantesGrimpantes?.actif && (
              <div>
                <div style={rowStyle}>
                  <span style={{ width: 90 }}>Hauteur</span>
                  <input className="slider-ambiance" style={fieldStyle} type="range" min={0.1} max={1} step={0.05} value={params.vegetation.plantesGrimpantes!.hauteur!} onChange={e => setParams(p => ({ ...p, vegetation: { ...p.vegetation!, plantesGrimpantes: { ...p.vegetation!.plantesGrimpantes!, hauteur: parseFloat(e.target.value) } } }))} />
                </div>
                <div style={rowStyle}>
                  <span style={{ width: 90 }}>Couleur</span>
                  <input style={fieldStyle} type="color" value={params.vegetation.plantesGrimpantes!.couleur!} onChange={e => setParams(p => ({ ...p, vegetation: { ...p.vegetation!, plantesGrimpantes: { ...p.vegetation!.plantesGrimpantes!, couleur: e.target.value } } }))} />
                </div>
              </div>
            )}
            </div>
          </div>
        )}
      </div>

      <div style={sectionStyle}>
        <div style={{
          fontSize: '16px',
          fontWeight: 'bold',
          marginBottom: '10px',
          color: '#4dabf7',
          borderBottom: '2px solid #4dabf7',
          paddingBottom: '3px'
        }}>🪑 Mobilier</div>
        <label style={labelStyle}><input type="checkbox" checked={!!params.mobilier?.actif} onChange={toggle('mobilier')} /> Activer</label>
        {params.mobilier?.actif && (
          <div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <button onClick={() => addMobilier(TypeMobilier.TABLE)}>+ Table</button>
              <button onClick={() => addMobilier(TypeMobilier.CHAISE)}>+ Chaise</button>
              <button onClick={() => addMobilier(TypeMobilier.CANAPE)}>+ Canapé</button>
              <button onClick={() => addMobilier(TypeMobilier.BANC)}>+ Banc</button>
              <button onClick={() => addMobilier(TypeMobilier.PARASOL)}>+ Parasol</button>
            </div>
            {(params.mobilier.elements || []).map(el => (
              <div key={el.id} style={{ ...sectionStyle, background: '#1a1a1a', border: '1px solid #555' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <strong>{el.type}</strong>
                  <div>
                    <label style={{ marginRight: 8 }}>
                      <input type="checkbox" checked={el.visible !== false} onChange={e => updateElement(el.id, { visible: e.target.checked })} /> Visible
                    </label>
                    <button onClick={() => removeElement(el.id)} style={{ color: '#b00' }}>Supprimer</button>
                  </div>
                </div>
                <div style={rowStyle}>
                  <span style={{ width: 90 }}>Position X</span>
                  <input style={fieldStyle} type="number" value={el.position.x} step={0.1} onChange={e => updateElement(el.id, { position: { ...el.position, x: parseFloat(e.target.value) } })} />
                </div>
                <div style={rowStyle}>
                  <span style={{ width: 90 }}>Position Z</span>
                  <input style={fieldStyle} type="number" value={el.position.z} step={0.1} onChange={e => updateElement(el.id, { position: { ...el.position, z: parseFloat(e.target.value) } })} />
                </div>
                <div style={rowStyle}>
                  <span style={{ width: 90 }}>Rotation</span>
                  <input className="slider-ambiance" style={fieldStyle} type="range" min={-180} max={180} value={el.rotation} onChange={e => updateElement(el.id, { rotation: parseFloat(e.target.value) })} />
                </div>
                <div style={rowStyle}>
                  <span style={{ width: 90 }}>Echelle</span>
                  <input className="slider-ambiance" style={fieldStyle} type="range" min={0.5} max={2} step={0.05} value={el.echelle || 1} onChange={e => updateElement(el.id, { echelle: parseFloat(e.target.value) })} />
                </div>
                <div style={rowStyle}>
                  <span style={{ width: 90 }}>Couleur</span>
                  <input style={fieldStyle} type="color" value={el.couleur || '#888888'} onChange={e => updateElement(el.id, { couleur: e.target.value })} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>

)};

export default AccessoiresModule;